import { type CSSProperties, useEffect, useState } from 'react';
import { useSyncGate } from './SyncGateProvider';

// ─── Constants ───

const appName = import.meta.env.VITE_APP_NAME || 'PowerCare AI';
const SLOW_HINT_DELAY_MS = 8_000;

// ─── Warning Banner ───

function WarningBanner({ title, message }: { title: string; message: string }) {
  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--sp-4, 16px)',
        background: '#fffbeb',
        border: '1px solid #f59e0b',
        borderRadius: 'var(--radius-lg, 14px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2, 8px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2, 8px)',
        }}
      >
        <span style={{ fontSize: '16px', lineHeight: 1 }}>&#9888;</span>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: '#92400e',
          }}
        >
          {title}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-xs, 12px)',
          lineHeight: 'var(--line-height-sm, 20px)',
          color: '#78350f',
        }}
      >
        {message}
      </p>
    </div>
  );
}

// ─── Milestone Item ───

function MilestoneItem({ done, label, count }: { done: boolean; label: string; count?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: 'var(--radius-full, 9999px)',
          fontSize: '12px',
          flexShrink: 0,
          transition: 'background 0.3s, color 0.3s',
          ...(done
            ? {
                background: 'var(--color-success, #22c55e)',
                color: '#fff',
              }
            : {
                background: 'var(--bg-surface-2, #f1f5f9)',
                color: 'var(--text-muted, #64748b)',
                border: '1.5px solid var(--border, rgba(15,23,42,0.12))',
              }),
        }}
      >
        {done ? '\u2713' : '\u25CB'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm, 14px)',
          lineHeight: 'var(--line-height-sm, 20px)',
          color: done ? 'var(--text, #0f172a)' : 'var(--text-muted, #64748b)',
          fontWeight: done ? 'var(--font-weight-medium, 500)' : 'var(--font-weight-regular, 400)',
          transition: 'color 0.3s',
          flex: 1,
        }}
      >
        {label}
      </span>
      {count != null && (
        <span
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--text-placeholder, #94a3b8)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

// ─── Progress Bar ───

function ProgressBar({ value }: { value: number }) {
  const barStyle: CSSProperties = {
    width: '100%',
    height: 6,
    borderRadius: 'var(--radius-full, 9999px)',
    background: 'var(--bg-surface-2, #f1f5f9)',
    overflow: 'hidden',
  };

  const fillStyle: CSSProperties = {
    width: `${value}%`,
    height: '100%',
    borderRadius: 'var(--radius-full, 9999px)',
    background: 'var(--gradient-cta, linear-gradient(135deg, #2563eb, #1d4ed8))',
    transition: 'width 0.5s ease',
  };

  return (
    <div style={barStyle}>
      <div style={fillStyle} />
    </div>
  );
}

// ─── Action Button ───

function ActionButton({
  label,
  onClick,
  disabled,
  variant = 'primary',
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'danger';
}) {
  const isPrimary = variant === 'primary';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 20px',
        borderRadius: 'var(--radius-md, 10px)',
        border: 'none',
        fontSize: 'var(--font-size-sm, 14px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'opacity 0.2s',
        ...(isPrimary
          ? {
              background: 'var(--gradient-cta, linear-gradient(135deg, #2563eb, #1d4ed8))',
              color: '#fff',
            }
          : {
              background: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fecaca',
            }),
      }}
    >
      {label}
    </button>
  );
}

// ─── Recovery Mode Panel ───

function RecoveryPanel() {
  const { sync, uploadQueue, retrySync, forceResync, isRetrying } = useSyncGate();

  const [confirmForce, setConfirmForce] = useState(false);

  const handleForceResync = () => {
    if (uploadQueue.hasPending && !confirmForce) {
      setConfirmForce(true);
      return;
    }
    forceResync();
  };

  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--sp-4, 16px)',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 'var(--radius-lg, 14px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3, 12px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)' }}>
        <span style={{ fontSize: '16px', lineHeight: 1 }}>&#9888;</span>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: '#991b1b',
          }}
        >
          Sync is taking longer than expected
        </span>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-xs, 12px)',
          lineHeight: 'var(--line-height-sm, 20px)',
          color: '#7f1d1d',
        }}
      >
        Some data hasn't synced yet. Try reconnecting, or clear the local database to start fresh.
      </p>

      {/* Diagnostics */}
      <div
        style={{
          fontSize: 'var(--font-size-xs, 12px)',
          color: '#7f1d1d',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1, 4px)',
        }}
      >
        <span>
          Milestones: {sync.milestones.filter((m) => m.done).length}/{sync.milestones.length}{' '}
          complete
        </span>
        {uploadQueue.hasPending && <span>Pending uploads: {uploadQueue.count}</span>}
      </div>

      {/* Confirm force resync warning */}
      {confirmForce && uploadQueue.hasPending && (
        <div
          style={{
            padding: 'var(--sp-3, 12px)',
            background: '#fff',
            border: '1px solid #fecaca',
            borderRadius: 'var(--radius-md, 10px)',
            fontSize: 'var(--font-size-xs, 12px)',
            color: '#991b1b',
          }}
        >
          You have {uploadQueue.count} pending upload(s) that will be lost. Click Force Resync again
          to confirm.
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 'var(--sp-3, 12px)' }}>
        <ActionButton
          label={isRetrying ? 'Retrying...' : 'Retry Sync'}
          onClick={retrySync}
          disabled={isRetrying}
          variant="primary"
        />
        <ActionButton
          label={isRetrying ? 'Clearing...' : 'Force Resync'}
          onClick={handleForceResync}
          disabled={isRetrying}
          variant="danger"
        />
      </div>
    </div>
  );
}

// ─── Main Component ───

export function InitialSyncScreen() {
  const { phase, sync, browserSupport, syncError } = useSyncGate();
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(true), SLOW_HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const isRecovery = phase === 'recovery';
  const hasWarning = !browserSupport.supported || syncError != null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-app, #f8fafc)',
        zIndex: 9999,
        fontFamily: 'var(--font-family, system-ui, -apple-system, sans-serif)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          padding: 'var(--sp-8, 32px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--sp-6, 24px)',
        }}
      >
        {/* Brand */}
        <div
          style={{
            fontSize: 'var(--font-size-lg, 18px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--color-primary, #2563eb)',
            letterSpacing: '-0.3px',
          }}
        >
          {appName}
        </div>

        {/* Title & Subtitle */}
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2, 8px)',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xl, 20px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              lineHeight: 'var(--line-height-xl, 28px)',
              letterSpacing: '-0.4px',
              color: 'var(--text, #0f172a)',
            }}
          >
            {isRecovery ? 'Sync needs attention' : 'Setting up your workspace...'}
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 'var(--line-height-sm, 20px)',
              color: 'var(--text-muted, #64748b)',
            }}
          >
            {isRecovery
              ? 'Some data could not be synced in time.'
              : 'Syncing clinic data for the first time.'}
          </p>
        </div>

        {/* Progress Bar */}
        <div style={{ width: '100%' }}>
          <ProgressBar value={sync.progress} />
        </div>

        {/* Milestones */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
            padding: 'var(--sp-4, 16px)',
            background: 'var(--bg-surface, #ffffff)',
            border: '1px solid var(--border, rgba(15,23,42,0.12))',
            borderRadius: 'var(--radius-lg, 14px)',
          }}
        >
          {sync.milestones.map((m) => (
            <MilestoneItem
              key={m.key}
              done={m.done}
              label={m.label}
              count={isRecovery ? m.count : undefined}
            />
          ))}
        </div>

        {/* Recovery Mode Panel */}
        {isRecovery && <RecoveryPanel />}

        {/* Browser support warning */}
        {!browserSupport.supported && (
          <WarningBanner
            title="Unsupported browser detected"
            message={`${browserSupport.browserName} is missing ${browserSupport.missing.join(' and ')} required for data sync. Please use a modern desktop browser (Chrome, Edge, or Firefox) for the full experience.`}
          />
        )}

        {/* Sync init error warning */}
        {syncError != null && browserSupport.supported && (
          <WarningBanner
            title="Sync initialization failed"
            message="Data sync could not initialize on this device. The app will continue without offline sync capabilities."
          />
        )}

        {/* Slow sync hint (only when no warnings and not in recovery) */}
        {showHint && !hasWarning && !isRecovery && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-placeholder, #94a3b8)',
              textAlign: 'center',
              animation: 'fadeIn 0.3s ease',
            }}
          >
            This may take a moment on slower connections.
          </p>
        )}
      </div>
    </div>
  );
}
